/**
 * VideosApi — Video methods (unified content API surface).
 *
 * Extracted from client.ts (CUR-547 god-object split). Methods-only by
 * design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { VideoAnalysis, VideoDetailResponse, VideoEntity, VideoOutline, VideoRelationship, VideoTranscript } from '../../../../shared/types/api';
import { APIError as SharedAPIError, DEFAULT_RETRY_OPTIONS, buildApiUrl, createAuthHeaders, processApiResponse, withRetry } from '../../../../shared/utils/api-helpers';
import { API_ENDPOINTS } from '@/utils/env';

import { ArticlesApi } from './articles-api';

// Extends ArticlesApi (not BaseApiClient directly): several video methods
// resolve an article to its content_id via this.getArticle(). applyMixins
// copies own-prototype methods only, so getArticle is merged once (from
// ArticlesApi), not twice.
export class VideosApi extends ArticlesApi {
  // ============================================================================
  // Video Methods (Unified Content API)
  // ============================================================================

  /**
   * Get video detail by video ID (direct lookup)
   * Uses the new /api/videos/{video_id} endpoint
   */
  async getVideoDetail(videoId: number): Promise<VideoDetailResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.video(videoId));
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchVideo = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<VideoDetailResponse>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchVideo, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * Get video analysis by article ID
   * Uses unified /api/content/{id}/analysis endpoint
   */
  async getVideoAnalysis(articleId: number): Promise<VideoAnalysis> {
    // First get the article to find content_id
    const article = await this.getArticle(articleId);
    const contentId = article.content_id;

    if (!contentId) {
      throw new SharedAPIError(404, 'Content ID not found for article', 'CONTENT_ID_MISSING');
    }

    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/analysis`);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchAnalysis = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<VideoAnalysis>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchAnalysis, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * Get video transcript by article ID
   * Uses unified /api/content/{id}/transcript endpoint
   */
  async getVideoTranscript(articleId: number): Promise<VideoTranscript> {
    // First get the article to find content_id
    const article = await this.getArticle(articleId);
    const contentId = article.content_id;

    if (!contentId) {
      throw new SharedAPIError(404, 'Content ID not found for article', 'CONTENT_ID_MISSING');
    }

    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/transcript`);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchTranscript = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<VideoTranscript>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchTranscript, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * Get video outline by article ID
   * Uses unified /api/content/{id}/analysis endpoint and extracts outline
   *
   * FIX (2025-11-21): The analysis endpoint returns a full bundle with outline nested.
   * We must extract the outline field to match the VideoOutline type.
   */
  async getVideoOutline(articleId: number): Promise<VideoOutline> {
    // First get the article to find content_id
    const article = await this.getArticle(articleId);
    const contentId = article.content_id;

    if (!contentId) {
      throw new SharedAPIError(404, 'Content ID not found for article', 'CONTENT_ID_MISSING');
    }

    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/analysis`);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchOutline = async () => {
      const response = await fetch(url, { headers });
      // Analysis endpoint returns: { content_id, content_type, video_id, outline, transcript, entities, relationships }
      const result = await processApiResponse<any>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      // Extract the outline field from the analysis response
      const analysisData = result.data!;
      if (!analysisData.outline) {
        throw new SharedAPIError(404, 'Outline not found in analysis response', 'OUTLINE_MISSING');
      }

      return analysisData.outline as VideoOutline;
    };

    return withRetry(fetchOutline, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * Get video entities by article ID
   * Uses unified /api/content/{id}/entities endpoint
   */
  async getVideoEntities(articleId: number): Promise<VideoEntity[]> {
    // First get the article to find content_id
    const article = await this.getArticle(articleId);
    const contentId = article.content_id;

    if (!contentId) {
      throw new SharedAPIError(404, 'Content ID not found for article', 'CONTENT_ID_MISSING');
    }

    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/entities`);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchEntities = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<VideoEntity[]>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchEntities, DEFAULT_RETRY_OPTIONS);
  }

  /**
   * Get video relationships by article ID
   * Uses unified /api/content/{id}/relationships endpoint
   */
  async getVideoRelationships(articleId: number): Promise<VideoRelationship[]> {
    // First get the article to find content_id
    const article = await this.getArticle(articleId);
    const contentId = article.content_id;

    if (!contentId) {
      throw new SharedAPIError(404, 'Content ID not found for article', 'CONTENT_ID_MISSING');
    }

    const url = buildApiUrl(this.baseUrl, `${API_ENDPOINTS.content}/${contentId}/relationships`);
    const headers = createAuthHeaders(await this.resolveToken());

    const fetchRelationships = async () => {
      const response = await fetch(url, { headers });
      const result = await processApiResponse<VideoRelationship[]>(response);

      if (result.error) {
        throw new SharedAPIError(result.status, result.error.message, result.error.code);
      }

      return result.data!;
    };

    return withRetry(fetchRelationships, DEFAULT_RETRY_OPTIONS);
  }

}
